import { useState, useMemo } from "react";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";
import { useListQueues, type Queue } from "@/lib/api/system";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { UpdateQueueModal } from "./components/UpdateQueueModal";

export function QueuesDashboard() {
  const listResult = useListQueues();
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState<Queue | null>(null);

  const queuesData = useMemo(() => {
    return listResult?.data?.data || [];
  }, [listResult]);

  const columns = useMemo((): DataTableColumn<Queue>[] => [
    {
      id: "id",
      accessorKey: "id",
      header: "ID",
    },
    {
      id: "name",
      accessorKey: "name",
      header: "Name",
      size: 270,
      minSize: 200,
    },
    {
      id: "type",
      accessorKey: "type",
      header: "Type",
    },
    {
      id: "max_concurrent",
      accessorKey: "max_concurrent",
      header: "Max Concurrent",
      cell: ({ row }) => {
        const value = row.original.max_concurrent;
        return value === null ? "null" : value;
      },
    },
    {
      id: "created_at",
      accessorKey: "created_at",
      header: "Created At",
      cell: ({ row }) => {
        return moment
          .tz(row.original.created_at, CommonDestinationTimezone)
          .format("YYYY-MM-DD HH:mm:ss");
      },
    },
  ], []);

  const handleRowClick = (row: Queue) => {
    setSelectedQueue(row);
    setIsUpdateModalOpen(true);
  };

  const handleSuccess = () => {
    listResult.refetch();
  };

  const isLoading = listResult.isLoading;

  return (
    <div className="flex flex-col gap-4 h-full overflow-hidden">
      <DataTable
        data={queuesData}
        columns={columns}
        enableSorting
        enableColumnResizing
        emptyMessage={isLoading ? "Loading..." : "No queues found"}
        rowClickable
        onRowClick={handleRowClick}
      />

      <UpdateQueueModal
        isOpen={isUpdateModalOpen}
        onClose={() => {
          setIsUpdateModalOpen(false);
          setSelectedQueue(null);
        }}
        onSuccess={handleSuccess}
        queue={selectedQueue}
      />
    </div>
  );
}
